import React from "react";
import { agreementTermSections } from "./agreementTermSections";
import { Link } from "react-router-dom";
import { SavedSignature } from "components/DrawnSignature";
import { formatAbn } from "utils/abn";
import { rctiTimestamp } from "utils/rctiTimestamp";
import { agreementReference, agreementLabel } from "./RctiDesignSystem";
import "./agreementJourney.css";

export function AgreementTerms({ terms = "", structured = true }) {
  if (structured)
    return (
      <div className="agreement-clauses agreement-clauses--structured">
        {agreementTermSections(terms).map((section) => (
          <section className="agreement-clause" key={section.title}>
            <h3>{section.title}</h3>
            <p>{section.paragraphs.join(" ")}</p>
          </section>
        ))}
      </div>
    );
  return (
    <div className="agreement-clauses">
      {terms
        .split(/(?<=\.)\s+/)
        .filter(Boolean)
        .map((text, index) => (
          <p key={index}>{text}</p>
        ))}
    </div>
  );
}
const activityNames = {
  proposed: "Buyer signed and proposed agreement",
  accepted: "Supplier accepted agreement",
  rejected: "Supplier rejected invitation",
  suspended: "Agreement suspended",
  expired: "Unsigned invitation expired",
  invitation: "Invitation sent",
};
export function AgreementOverview({
  agreement: a,
  actions,
  primaryAction,
  audience = "buyer",
  onClose,
  children,
}) {
  const signed = Boolean(a.supplierSignature && a.supplierSignedAt);
  return (
    <div className={`agreement-overview agreement-overview--${audience}`}>
      <div className="agreement-overview-status">
        <span
          className={`rcti-agreement-badge rcti-agreement-badge--${a.status}`}
        >
          {agreementLabel(a.status)}
        </span>
        <span>
          Wording v{a.version} ·{" "}
          {signed
            ? `Accepted ${rctiTimestamp(a.supplierSignedAt)}`
            : `Prepared ${rctiTimestamp(a.createdAt || a.buyerSignedAt)}`}
        </span>
        {primaryAction && (
          <div className="agreement-overview-primary">{primaryAction}</div>
        )}
        {a.status === "active" && audience === "buyer" && (
          <Link
            className="rcti-design-primary agreement-overview-primary"
            to="/rcti/add"
            state={{ rctiSupplierID: a.contactID }}
          >
            Create / resume RCTI
          </Link>
        )}
      </div>
      {!signed && (
        <div className="rcti-design-notice">
          <strong>Supplier acceptance is still required</strong>
          <p>
            No supplier signature is recorded. This agreement is unavailable for
            new RCTIs until acceptance and eligibility checks pass.
            {a.invitationExpiresAt
              ? ` Sign by ${rctiTimestamp(a.invitationExpiresAt)}.`
              : ""}
          </p>
        </div>
      )}
      {signed && a.status !== "active" && (
        <div className="rcti-design-notice">
          <strong>New issues and adjustments are blocked</strong>
          <p>
            Earlier issued documents and recorded payments remain in history.
          </p>
        </div>
      )}
      <div className="agreement-overview-columns">
        <div className="rcti-design-stack">
          <section className="rcti-agreement-card">
            <h2>Parties</h2>
            <div className="rcti-design-party-grid">
              {[
                ["Buyer / issuer", a.buyer, a.buyerRepresentative],
                [
                  "Supplier / vendor",
                  a.supplier,
                  a.supplierSigner || a.supplierRepresentative,
                ],
              ].map(([label, party, person]) => (
                <div key={label}>
                  <small>{label}</small>
                  <h3>{party.name}</h3>
                  <p>ABN {formatAbn(party.abnNumber)}</p>
                  {person?.name && (
                    <p>
                      {person.name} · {person.position}
                    </p>
                  )}
                  <p>{party.emailAddress}</p>
                  {party.address && <p>{party.address}</p>}
                </div>
              ))}
            </div>
          </section>
          <section className="rcti-agreement-card">
            <h2>Scope and duration</h2>
            <dl className="agreement-facts">
              <dt>Covered supplies</dt>
              <dd>{a.scope}</dd>
              <dt>Value / tax basis</dt>
              <dd>Buyer determines the value · All covered supplies taxable</dd>
              <dt>Prepared</dt>
              <dd>{rctiTimestamp(a.createdAt || a.buyerSignedAt)}</dd>
              <dt>Effective</dt>
              <dd>
                {signed
                  ? `${rctiTimestamp(
                      a.supplierSignedAt
                    )} · On supplier acceptance`
                  : "Not effective · Awaiting supplier acceptance"}
              </dd>
              <dt>Duration</dt>
              <dd>No fixed expiry · Either party can suspend</dd>
            </dl>
            <section className="agreement-saved-terms">
              <h3>
                {signed ? "View accepted" : "Review proposed"} wording v
                {a.version}
              </h3>
              <AgreementTerms terms={a.terms} />
            </section>
          </section>
          <section className="rcti-agreement-card">
            <h2>Signatures and accepted version</h2>
            <p>
              {signed
                ? `Accepted version v${a.version} · Accepted exactly as invited`
                : `Not yet accepted · Proposed wording v${a.version}`}
            </p>
            <div className="agreement-signature-records">
              {[
                [
                  "Buyer",
                  a.buyer,
                  a.buyerRepresentative,
                  a.buyerSignature,
                  a.buyerSignedAt,
                ],
                [
                  "Supplier",
                  a.supplier,
                  a.supplierSigner,
                  a.supplierSignature,
                  a.supplierSignedAt,
                ],
              ].map(([label, party, person, signature, time]) => (
                <div key={label}>
                  <h3>
                    {label} · {person?.name || party.name}
                  </h3>
                  <p>{person?.position || "Authorised representative"}</p>
                  <p>{party.emailAddress}</p>
                  {signature ? (
                    <SavedSignature
                      value={signature}
                      label={`${label} signature`}
                    />
                  ) : (
                    <p>Signature not recorded · Acceptance pending</p>
                  )}
                  <small>
                    {time
                      ? `${
                          label === "Supplier" ? "Accepted" : "Signed"
                        } ${rctiTimestamp(time)} · Wording v${a.version}`
                      : "Not yet signed"}
                  </small>
                </div>
              ))}
            </div>
            <p>
              GST registration, notification and no-duplicate invoice
              obligations form part of the saved wording.
            </p>
          </section>
        </div>
        <aside className="rcti-design-sidebar">
          <section className="rcti-agreement-card">
            <h2>Agreement management</h2>
            <p>
              Actions apply to {agreementReference(a._id)}. Suspension blocks
              future issue and adjustments; existing records remain intact.
            </p>
            <details className="agreement-management-menu">
              <summary>Manage agreement</summary>
              {actions}
              <div className="agreement-delivery-record">
                <h3>Email delivery</h3>
                <p>Invitation: {a.invitationDelivery || "pending"}</p>
                {signed && (
                  <>
                    <p>
                      Buyer confirmation: {a.ownerEmailDelivery || "pending"}
                    </p>
                    <p>
                      Supplier suspension link:{" "}
                      {a.supplierEmailDelivery || "pending"}
                    </p>
                  </>
                )}
              </div>
            </details>
          </section>
          {children}
          <section className="rcti-agreement-card">
            <h2>Agreement activity</h2>
            <ol className="rcti-design-timeline">
              {(a.events || []).length ? (
                [...a.events].reverse().map((event, index) => (
                  <li key={index}>
                    <small>{rctiTimestamp(event.at)}</small>
                    <strong>
                      {activityNames[event.action] || event.action}
                    </strong>
                    {event.reason && <p>{event.reason}</p>}
                  </li>
                ))
              ) : (
                <li>
                  <p>No activity recorded.</p>
                </li>
              )}
            </ol>
          </section>
        </aside>
      </div>
      <footer className="agreement-overview-footer">
        {audience === "supplier" ? (
          <button type="button" onClick={onClose}>
            Close agreement
          </button>
        ) : (
          <Link to="/rcti-agreements">Back to agreements</Link>
        )}
        {audience === "supplier" ? (
          signed ? (
            primaryAction
          ) : null
        ) : a.status === "active" ? (
          <Link
            className="rcti-design-primary"
            to="/rcti/add"
            state={{ rctiSupplierID: a.contactID }}
          >
            Create / resume RCTI
          </Link>
        ) : (
          <button disabled>Create RCTI unavailable</button>
        )}
      </footer>
    </div>
  );
}
export function AgreementVerification({
  data,
  selected,
  onSupplier,
  onManageContact,
  onContinue,
}) {
  const existing = data.agreements.find(
    (a) =>
      String(a.contactID) === String(selected?._id) && a.status === "active"
  );
  return (
    <div className="agreement-verification">
      <div className="agreement-verification-parties">
        {[
          [
            "Buyer / issuer",
            data.buyer,
            "/MyProfile?returnTo=%2Frcti-agreements%2Fnew",
          ],
          [
            "Supplier / vendor",
            selected,
            `/contacts/editContact/${selected?._id}`,
          ],
        ].map(([label, party, url]) => (
          <section className="rcti-agreement-card" key={label}>
            <h2>{label}</h2>
            <h3>{party?.companyName || party?.name || "Select a supplier"}</h3>
            <dl className="agreement-facts">
              <dt>ABN</dt>
              <dd>{formatAbn(party?.abnNumber) || "Not recorded"}</dd>
              <dt>GST registration</dt>
              <dd>
                {party?.gstRegistered === true
                  ? "Declared registered"
                  : "Confirmation required"}
              </dd>
              <dt>Large business</dt>
              <dd>
                {party?.isLargeBusiness === false
                  ? "Declared no"
                  : party?.isLargeBusiness === true
                  ? "Declared yes"
                  : "Not confirmed"}
              </dd>
              <dt>Government entity</dt>
              <dd>
                {party?.isGovernmentEntity === false
                  ? "Declared no"
                  : party?.isGovernmentEntity === true
                  ? "Declared yes"
                  : "Not confirmed"}
              </dd>
            </dl>
            <Link
              to={
                label.startsWith("Buyer")
                  ? url
                  : `${url}?returnTo=%2Frcti-agreements%2Fnew`
              }
              onClick={(event) => {
                if (!label.startsWith("Buyer") && onManageContact) {
                  event.preventDefault();
                  onManageContact(selected?._id);
                }
              }}
            >
              Edit {label.startsWith("Buyer") ? "business" : "contact"}{" "}
              eligibility
            </Link>
          </section>
        ))}
      </div>
      <section className="rcti-agreement-card">
        <h2>Buyer eligibility declaration</h2>
        <p>
          The buyer determines the value of the covered supplies. Every covered
          line must be taxable with GST. Business and contact eligibility
          declarations above are saved settings; they are not a new live
          registry result.
        </p>
        {selected?.reasons.length ? (
          <ul className="rcti-agreements__error">
            {selected.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : (
          <p>
            Current saved eligibility settings satisfy this pathway. The server
            checks them again before sending and issuing.
          </p>
        )}
      </section>
      <section className="rcti-agreement-card">
        <h2>Agreement check</h2>
        {existing ? (
          <>
            <strong>Active agreement available</strong>
            <p>Review its scope before preparing another supply.</p>
            <Link
              className="rcti-design-primary"
              to={`/rcti-agreements/${existing._id}`}
            >
              View active agreement
            </Link>
          </>
        ) : (
          <>
            <strong>New agreement needed</strong>
            <p>
              Prepare the contract and invite the supplier to sign. Sending
              alone does not activate it.
            </p>
          </>
        )}
        <button
          type="button"
          className="rcti-button-secondary"
          onClick={onSupplier}
        >
          Change supplier
        </button>
      </section>
      <footer className="agreement-editor-footer">
        <button
          type="button"
          className="rcti-button-secondary"
          onClick={onSupplier}
        >
          ← Back to supplier
        </button>
        {existing ? (
          <Link
            className="rcti-design-primary"
            to="/rcti/add"
            state={{ rctiSupplierID: selected?._id }}
          >
            Prepare RCTI
          </Link>
        ) : (
          <button
            type="button"
            disabled={
              !data.canWrite || !selected || selected.reasons.length > 0
            }
            onClick={onContinue}
          >
            Continue to agreement
          </button>
        )}
      </footer>
    </div>
  );
}
